import type { HarnessInvoice } from "@/api/providers/invoices";
import type { HarnessPaymentMethod } from "@/api/providers/payment-methods";
import type {
  HarnessLineItemRecurringBillingFrequency,
  HarnessRecurringBillingFrequency,
  HarnessSubscription,
  HarnessSubscriptionStatus,
} from "@/api/providers/subscriptions";
import { parseBusinessDateOnly } from "@/plugins/controls/common/subscription-business-date";

export enum UiInvoiceStatus {
  Paid = "paid",
  Due = "due",
  Overdue = "overdue",
}

export enum UiInvoiceStatusSummary {
  Paid = "paid",
  Due = "due",
  Overdue = "overdue",
  None = "none",
}

export enum UiSubscriptionStatus {
  Active = "active",
  PastDue = "past-due",
  Scheduled = "scheduled",
  Canceled = "canceled",
  Expired = "expired",
  Paused = "paused",
}

const invoiceStatusStyles = {
  [UiInvoiceStatusSummary.Paid]: "bg-emerald-100 text-emerald-800",
  [UiInvoiceStatusSummary.Due]: "bg-amber-100 text-amber-900",
  [UiInvoiceStatusSummary.Overdue]: "bg-rose-100 text-rose-800",
  [UiInvoiceStatusSummary.None]: "bg-muted text-muted-foreground",
} satisfies Record<UiInvoiceStatusSummary, string>;

export type UiInvoice = {
  id: string;
  providerInvoiceId: string;
  subscriptionId: string;
  subscriptionName: string;
  periodLabel: string;
  issueDate: string | null;
  dueDate: string | null;
  amountCents: number;
  status: UiInvoiceStatus;
  isVoided: boolean;
};

export type InvoiceStatusDisplay = {
  key: UiInvoiceStatusSummary;
  label: string;
  className: string;
};

export type UiSubscriptionProduct = {
  id: string;
  name: string;
  amountCents: number;
  recurringBillingFrequency: HarnessLineItemRecurringBillingFrequency | null;
};

export type UiSubscription = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  nextInvoiceDate: string | null;
  products: UiSubscriptionProduct[];
  monthlyAmountCents: number;
  recurrenceInterval: HarnessRecurringBillingFrequency;
  status: UiSubscriptionStatus | `status-${string}`;
};

export enum UiPaymentMethodType {
  Card = "card",
  Ach = "ach",
}

export type UiBankAccountType = "checking" | "savings";

export type UiCardPaymentMethod = {
  id: number;
  type: UiPaymentMethodType.Card;
  nickname: string | null;
  brand: string;
  cardCategory: string | null;
  last4: string;
  expMonth: string;
  expYear: string;
  billingName: string;
  billingZip: string;
  isDefault: boolean;
  isDisabled: boolean;
};

export type UiAchPaymentMethod = {
  id: number;
  type: UiPaymentMethodType.Ach;
  nickname: string | null;
  bankName: string;
  accountType: UiBankAccountType;
  accountLast4: string;
  routingLast4: string;
  billingName: string;
  billingZip: string;
  isDefault: boolean;
  isDisabled: boolean;
};

export type UiPaymentMethod = UiCardPaymentMethod | UiAchPaymentMethod;

function amountToCents(amount: number | null | undefined) {
  return Math.round((amount ?? 0) * 100);
}

function normalizeSubscriptionDateOnly(
  dateText: string | null | undefined,
): string | null {
  if (!dateText) {
    return null;
  }

  return parseBusinessDateOnly(dateText) ? dateText : null;
}

function formatPeriod(dateText: string | null) {
  if (!dateText) {
    return "N/A";
  }

  const parsedDate = new Date(dateText);
  if (Number.isNaN(parsedDate.getTime())) {
    return "N/A";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function normalizeDigits(value: string | null | undefined) {
  return (value ?? "").replace(/\D/g, "");
}

function parseExpirationDate(value: string | null) {
  const digits = normalizeDigits(value);
  if (digits.length < 4) {
    return { month: "", year: "" };
  }

  const month = digits.slice(0, 2);
  const year =
    digits.length === 4 ? `20${digits.slice(2, 4)}` : digits.slice(2, 6);
  return { month, year };
}

function toUiBankAccountType(accountType: string | null): UiBankAccountType {
  return accountType?.toLowerCase() === "savings" ? "savings" : "checking";
}

function toUiPaymentMethodType(
  paymentMethodType: string | null,
  accountType: string | null,
  cardType: string | null,
) {
  const normalizedMethodType = (paymentMethodType ?? "").toLowerCase();
  const normalizedAccountType = (accountType ?? "").toLowerCase();
  const normalizedCardType = (cardType ?? "").trim();

  if (normalizedCardType.length > 0 && normalizedAccountType.length === 0) {
    return UiPaymentMethodType.Card;
  }

  if (
    normalizedMethodType.includes("ach") ||
    normalizedMethodType.includes("bank") ||
    normalizedMethodType.includes("checking") ||
    normalizedMethodType.includes("savings") ||
    normalizedAccountType.length > 0
  ) {
    return UiPaymentMethodType.Ach;
  }

  return UiPaymentMethodType.Card;
}

export function mapSubscriptionStatus(
  status: HarnessSubscriptionStatus,
): UiSubscriptionStatus | `status-${string}` {
  switch (status) {
    case "Active":
      return UiSubscriptionStatus.Active;
    case "PastDue":
    case "Unpaid":
      return UiSubscriptionStatus.PastDue;
    case "Scheduled":
      return UiSubscriptionStatus.Scheduled;
    case "Canceled":
      return UiSubscriptionStatus.Canceled;
    case "Expired":
      return UiSubscriptionStatus.Expired;
    case "Paused":
      return UiSubscriptionStatus.Paused;
    default:
      return `status-${status}`;
  }
}

export function getSubscriptionStatusLabel(
  status: UiSubscriptionStatus | `status-${string}`,
): string {
  switch (status) {
    case UiSubscriptionStatus.Active:
      return "Active";
    case UiSubscriptionStatus.PastDue:
      return "Past Due";
    case UiSubscriptionStatus.Scheduled:
      return "Scheduled";
    case UiSubscriptionStatus.Canceled:
      return "Canceled";
    case UiSubscriptionStatus.Expired:
      return "Expired";
    case UiSubscriptionStatus.Paused:
      return "Paused";
    default:
      return status
        .replace(/^status-/, "")
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
  }
}

export function formatSubscriptionBillingInterval(
  interval: HarnessRecurringBillingFrequency | HarnessLineItemRecurringBillingFrequency,
): string {
  switch (interval) {
    case "Daily":
      return "day";
    case "Weekly":
      return "week";
    case "Biweekly":
      return "every 2 weeks";
    case "Monthly":
      return "month";
    case "Quarterly":
      return "quarter";
    case "SemiAnnually":
    case "BiAnnually":
      return "every 6 months";
    case "Annually":
      return "year";
    case "EveryTwoYears":
      return "every 2 years";
    case "EveryThreeYears":
      return "every 3 years";
    case "EveryFourYears":
      return "every 4 years";
    case "EveryFiveYears":
      return "every 5 years";
    default:
      return String(interval).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  }
}

export function mapInvoiceStatus(invoice: HarnessInvoice): UiInvoiceStatus {
  if (invoice.invoiceStatus === "Paid") {
    return UiInvoiceStatus.Paid;
  }

  if (invoice.invoiceStatus === "Voided") {
    return UiInvoiceStatus.Paid;
  }

  if (invoice.dueDate) {
    const dueDate = new Date(invoice.dueDate);
    if (!Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now()) {
      return UiInvoiceStatus.Overdue;
    }
  }

  return UiInvoiceStatus.Due;
}

export function mapHarnessSubscriptionToUi(
  subscription: HarnessSubscription,
  index: number,
): UiSubscription {
  const id = subscription.subscriptionId ?? `subscription-${index}`;
  const products = (subscription.subscriptionLineItems ?? []).map(
    (item, itemIndex) => ({
      id: `${id}-line-item-${itemIndex}`,
      name: item.name ?? item.productCategory ?? `Line item ${itemIndex + 1}`,
      amountCents: amountToCents(item.amount),
      recurringBillingFrequency: item.recurringBillingFrequency,
    }),
  );

  return {
    id,
    name: subscription.subscriptionName ?? id,
    startDate: normalizeSubscriptionDateOnly(subscription.recurranceStartDate),
    endDate: normalizeSubscriptionDateOnly(subscription.recurranceEndDate),
    nextInvoiceDate: normalizeSubscriptionDateOnly(
      subscription.nextPaymentDueDate,
    ),
    products,
    monthlyAmountCents: amountToCents(subscription.nextPaymentAmount),
    recurrenceInterval: subscription.recurrenceInterval,
    status: mapSubscriptionStatus(subscription.status),
  };
}

export function mapHarnessInvoiceToUi(
  invoice: HarnessInvoice,
  index: number,
): UiInvoice {
  const providerInvoiceId =
    invoice.id ??
    invoice.invoiceNumber ??
    invoice.number ??
    `invoice-${index + 1}`;
  const invoiceId =
    invoice.invoiceNumber ??
    invoice.number ??
    invoice.id ??
    `invoice-${index + 1}`;
  const subscriptionId = invoice.subscriptionId ?? "unknown-subscription";

  return {
    id: invoiceId,
    providerInvoiceId,
    subscriptionId,
    subscriptionName: invoice.subscriptionName ?? subscriptionId,
    periodLabel: formatPeriod(invoice.invoiceDate),
    issueDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    amountCents: amountToCents(invoice.amountBilled),
    status: mapInvoiceStatus(invoice),
    isVoided: invoice.invoiceStatus === "Voided",
  };
}

export function mapHarnessPaymentMethodToUi(
  method: HarnessPaymentMethod,
): UiPaymentMethod {
  const last4 = normalizeDigits(method.maskedAccountNumber).slice(-4);
  const billingName = method.accountHolderName ?? "Account holder";
  const billingZip = method.billingZipCode ?? "";
  const uiMethodType = toUiPaymentMethodType(
    method.paymentMethodType,
    method.accountType,
    method.cardType,
  );
  const nickname = method.paymentMethodNickname;

  if (uiMethodType === UiPaymentMethodType.Ach) {
    return {
      id: method.id,
      type: UiPaymentMethodType.Ach,
      nickname,
      bankName: method.paymentMethodType ?? "Bank account",
      accountType: toUiBankAccountType(method.accountType),
      accountLast4: last4,
      routingLast4: "",
      billingName,
      billingZip,
      isDefault: method.isDefaultPaymentMethod,
      isDisabled: method.disabled,
    };
  }

  const expiration = parseExpirationDate(method.expirationDate);
  return {
    id: method.id,
    type: UiPaymentMethodType.Card,
    nickname,
    brand: method.paymentMethodType ?? "Card",
    cardCategory: method.cardCategory,
    last4,
    expMonth: expiration.month,
    expYear: expiration.year,
    billingName,
    billingZip,
    isDefault: method.isDefaultPaymentMethod,
    isDisabled: method.disabled,
  };
}

export function formatPaymentMethodSummary(
  method: UiPaymentMethod | null | undefined,
) {
  if (!method) {
    return "A saved payment method is required";
  }

  if (method.type === UiPaymentMethodType.Card) {
    if (method.nickname) {
      return `${method.nickname} - ${method.brand}`;
    }

    const expText =
      method.expMonth && method.expYear
        ? `${method.expMonth}/${method.expYear}`
        : "unknown";
    return `${method.brand} ending in ${method.last4 || "----"} (exp ${expText})`;
  }

  if (method.nickname) {
    return `${method.nickname} - ACH ${method.accountType} (${method.bankName})`;
  }
  return `ACH ${method.accountType} ending in ${method.accountLast4 || "----"} (${method.bankName})`;
}

export function getInvoiceStatusDisplay(
  invoice: UiInvoice,
): InvoiceStatusDisplay {
  if (invoice.isVoided) {
    return {
      key: UiInvoiceStatusSummary.Paid,
      label: "Void",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.Paid],
    };
  }

  if (invoice.status === UiInvoiceStatus.Paid) {
    return {
      key: UiInvoiceStatusSummary.Paid,
      label: "Paid",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.Paid],
    };
  }

  if (invoice.status === UiInvoiceStatus.Overdue) {
    return {
      key: UiInvoiceStatusSummary.Overdue,
      label: "Overdue",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.Overdue],
    };
  }

  return {
    key: UiInvoiceStatusSummary.Due,
    label: "Due",
    className: invoiceStatusStyles[UiInvoiceStatusSummary.Due],
  };
}

export function getInvoiceSummaryDisplay(
  invoices: UiInvoice[],
): InvoiceStatusDisplay {
  if (invoices.length === 0) {
    return {
      key: UiInvoiceStatusSummary.None,
      label: "No invoices",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.None],
    };
  }

  if (invoices.some((invoice) => invoice.status === UiInvoiceStatus.Overdue)) {
    return {
      key: UiInvoiceStatusSummary.Overdue,
      label: "Overdue",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.Overdue],
    };
  }

  if (invoices.every((invoice) => invoice.status === UiInvoiceStatus.Paid)) {
    return {
      key: UiInvoiceStatusSummary.Paid,
      label: "Paid",
      className: invoiceStatusStyles[UiInvoiceStatusSummary.Paid],
    };
  }

  return {
    key: UiInvoiceStatusSummary.Due,
    label: "Due",
    className: invoiceStatusStyles[UiInvoiceStatusSummary.Due],
  };
}
